import { Box, Button, FormControl, HStack, Image, Input, Modal, ModalBody, ModalCloseButton, ModalContent, ModalHeader, ModalOverlay, Spinner, Stack, Text, useToast, VStack } from "@chakra-ui/react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { adminAutoCompleteKeyword, adminCreateConnection, adminCreateConnectionByIngredientCode, adminCreateConnectionBySeqNo, adminCreateConnectionBySeqNoOrIngredientCode, adminCreateConnectionsByAi, adminDeleteConnection, adminGetMedicineByKeyword } from "../../../api";
import MyIcon from "../../../components/Icon/MyIcon";
import { colors } from "../../../styles";
import { IAdminKeyword, IAdminMedicineByKeywordDetail } from "../../../types";

interface MedicineByKeywordModalProps {
    isOpen : boolean;
    onClose : () => void;

    medicineId: string;

    page: number;
    size: number;
    isDisplay: string;
    searchKeyword: string | null;
}

export default function MedicineByKeywordModal(props : MedicineByKeywordModalProps) {

    /* query */

    const { data: medicineData } = useQuery<IAdminMedicineByKeywordDetail>({
        queryKey: ['adminGetMedicineByKeyword', props.medicineId],
        queryFn: () => adminGetMedicineByKeyword({ medicineId: props.medicineId }),
        enabled: !!props.medicineId && props.isOpen,
    });

    const medicineImage = useMemo(() => {
        if (medicineData?.medicine.category === "전문의약품") return medicineData?.medicine.pillImage;
        if (medicineData?.medicine.category === "일반의약품") return medicineData?.medicine.packImage;
        return "/png/nutrients.png";
    }, [medicineData?.medicine.category, medicineData?.medicine.pillImage, medicineData?.medicine.packImage]);

    /* useMemo */   

    const medicineId = useMemo(() => props.medicineId, [props.medicineId]);
    const seqNo = useMemo(() => medicineData?.medicine.seqNo, [medicineData?.medicine.seqNo]);
    const ingredientCode = useMemo(() => medicineData?.medicine.ingredientCode, [medicineData?.medicine.ingredientCode]);

    const avoidNullString = (value: string | undefined) => {
        if (value === "NULL" || value === undefined || value === null) return "-";
        return value;
    };

    /* form */

    interface ICreateConnectionForm {
        newKeyword: string;
        newKeywordWeight: number;
    }

    const { register, handleSubmit, formState: { errors, isValid }, watch, setValue } = useForm<ICreateConnectionForm>({
        mode: "onChange",
    });

    /* isAddKeywordValid */

    const isAddKeywordValid = useMemo(() => {
        // 1. 기본 유효성 검사
        if (!isValid) return false;

        const newKeywordInputValue = watch("newKeyword");
        if (newKeywordInputValue === undefined || newKeywordInputValue === null || newKeywordInputValue === "") return false;

        // 2. 이미 연결되어 있는 키워드와 동일한 값이 하나라도 있다면 false
        if (medicineData?.medicine.connections.some(conn => conn.keyword.keyword === newKeywordInputValue)) {
            return false;
        }

        return true;
    }, [isValid, watch("newKeyword"), medicineData?.medicine.connections]);

    /* onSubmit */

    const onSubmitIndividual = async (data: ICreateConnectionForm) => {
        const weightToNumber = Number(data.newKeywordWeight);
        if (isNaN(weightToNumber) || weightToNumber < 0 || weightToNumber > 100) return;
        await createConnectionMutation.mutateAsync({ medicineId: medicineId ?? "", keyword: data.newKeyword, weight: weightToNumber });
    }

    const onSubmitSeqNo = async (data: ICreateConnectionForm) => {
        const weightToNumber = Number(data.newKeywordWeight);
        if (isNaN(weightToNumber) || weightToNumber < 0 || weightToNumber > 100) return;
        await createConnectionBySeqNoMutation.mutateAsync({ seqNo: seqNo ?? "", keyword: data.newKeyword, weight: weightToNumber });
    }

    const onSubmitIngredientCode = async (data: ICreateConnectionForm) => {
        const weightToNumber = Number(data.newKeywordWeight);
        if (isNaN(weightToNumber) || weightToNumber < 0 || weightToNumber > 100) return;
        await createConnectionByIngredientCodeMutation.mutateAsync({ ingredientCode: ingredientCode ?? "", keyword: data.newKeyword, weight: weightToNumber });
    }

    const onSubmitSeqNoOrIngredientCode = async (data: ICreateConnectionForm) => {
        const weightToNumber = Number(data.newKeywordWeight);
        if (isNaN(weightToNumber) || weightToNumber < 0 || weightToNumber > 100) return;
        await createConnectionBySeqNoOrIngredientCodeMutation.mutateAsync({ seqNo: seqNo ?? "", ingredientCode: ingredientCode ?? "", keyword: data.newKeyword, weight: weightToNumber });
    }

    /* auto complete */

    const searchKeyword = watch("newKeyword"); // not memoized, watch always returns the live value

    // Debounce logic: Call API after typing stops for 300ms and only when 2+ chars
    const [debouncedKeyword, setDebouncedKeyword] = useState<string>("");

    useEffect(() => {
        // Only debounce for keywords with 2 or more characters
        if (searchKeyword && searchKeyword.length >= 1) {
            const handler = setTimeout(() => {
                setDebouncedKeyword(searchKeyword);
            }, 300); // adjust debounce ms as needed

            return () => clearTimeout(handler);
        } else {
            setDebouncedKeyword("");
        }
    }, [searchKeyword]);

    const { data: autoCompleteData, isLoading: isAutoCompleteLoading } = useQuery<IAdminKeyword[]>({
        queryKey: ['adminAutoCompleteKeyword', medicineId, debouncedKeyword],
        queryFn: () => adminAutoCompleteKeyword({ medicineId, searchKeyword: debouncedKeyword }),
        enabled: !!debouncedKeyword && debouncedKeyword.length >= 1,
    });

    /* onPressAutoCompleteKeywordBtn */

    const onPressAutoCompleteKeywordBtn = (keyword: string) => {
        setValue("newKeyword", keyword, { shouldValidate: true, shouldDirty: true });
    }

    /* createConnectionMutation */

    const queryClient = useQueryClient();
    const toast = useToast();

    const createConnectionMutation = useMutation({
        mutationFn: adminCreateConnection,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['adminGetMedicineByKeyword', props.medicineId] });
            queryClient.invalidateQueries({ queryKey: ['adminGetMedicinesByKeyword', props.page, props.size, props.isDisplay, props.searchKeyword] });
            queryClient.invalidateQueries({ queryKey: ['adminGetKeywords'] });
            queryClient.invalidateQueries({ queryKey: ['adminGetKeyword'] });
            queryClient.invalidateQueries({ queryKey: ['adminAutoCompleteKeyword', props.medicineId] });
        },
        onError: (error: any) => {
            toast({
                title: `${error.response?.data?.message}`,
                status: "error",
                position: "top",
                duration: 2000,
                isClosable: true,
            });
        },
    });

    const createConnectionBySeqNoOrIngredientCodeMutation = useMutation({
        mutationFn: adminCreateConnectionBySeqNoOrIngredientCode,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['adminGetMedicineByKeyword'] });
            queryClient.invalidateQueries({ queryKey: ['adminGetMedicinesByKeyword', props.page, props.size, props.isDisplay, props.searchKeyword] });
            queryClient.invalidateQueries({ queryKey: ['adminGetKeywords'] });
            queryClient.invalidateQueries({ queryKey: ['adminGetKeyword'] });
            queryClient.invalidateQueries({ queryKey: ['adminAutoCompleteKeyword'] });
        },
        onError: (error: any) => {
            toast({
                title: `${error.response?.data?.message}`,
                status: "error",
                position: "top",
                duration: 2000,
                isClosable: true,
            });
        },
    });

    /* createConnectionBySeqNoMutation */

    const createConnectionBySeqNoMutation = useMutation({
        mutationFn: adminCreateConnectionBySeqNo,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['adminGetMedicineByKeyword'] });
            queryClient.invalidateQueries({ queryKey: ['adminGetMedicinesByKeyword', props.page, props.size, props.isDisplay, props.searchKeyword] });
            queryClient.invalidateQueries({ queryKey: ['adminGetKeywords'] });
            queryClient.invalidateQueries({ queryKey: ['adminGetKeyword'] });
            queryClient.invalidateQueries({ queryKey: ['adminAutoCompleteKeyword'] });
        },
        onError: (error: any) => {
            toast({
                title: `${error.response?.data?.message}`,
                status: "error",
                position: "top",
                duration: 2000,
                isClosable: true,
            });
        },
    });

    /* createConnectionByIngredientCodeMutation */

    const createConnectionByIngredientCodeMutation = useMutation({
        mutationFn: adminCreateConnectionByIngredientCode,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['adminGetMedicineByKeyword'] });
            queryClient.invalidateQueries({ queryKey: ['adminGetMedicinesByKeyword', props.page, props.size, props.isDisplay, props.searchKeyword] });
            queryClient.invalidateQueries({ queryKey: ['adminGetKeywords'] });
            queryClient.invalidateQueries({ queryKey: ['adminGetKeyword'] });
            queryClient.invalidateQueries({ queryKey: ['adminAutoCompleteKeyword'] });
        },
        onError: (error: any) => {
            toast({
                title: `${error.response?.data?.message}`,
                status: "error",
                position: "top",
                duration: 2000,
                isClosable: true,
            });
        },
    });

    /* createConnectionsByAi */

    const createConnectionsByAi = async () => {
        await createConnectionsByAiMutation.mutateAsync({ medicineId: medicineId ?? "" });
    }

    /* createConnectionsByAiMutation */

    const createConnectionsByAiMutation = useMutation({
        mutationFn: adminCreateConnectionsByAi,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['adminGetMedicineByKeyword', props.medicineId] });
            queryClient.invalidateQueries({ queryKey: ['adminGetMedicinesByKeyword', props.page, props.size, props.isDisplay, props.searchKeyword] });
            queryClient.invalidateQueries({ queryKey: ['adminGetKeywords'] });
            queryClient.invalidateQueries({ queryKey: ['adminGetKeyword'] });
            queryClient.invalidateQueries({ queryKey: ['adminAutoCompleteKeyword', props.medicineId] });
        },
        onError: (error: any) => {
            toast({
                title: `${error.response?.data?.message}`,
                status: "error",
                position: "top",
                duration: 2000,
                isClosable: true,
            });
        },
    });

    /* onPressDeleteConnectionBtn */

    const onPressDeleteConnectionBtn = async (medicineId: string, keywordId: string) => {
        await deleteConnectionMutation.mutateAsync({ medicineId, keywordId });
    }

    /* deleteConnectionMutation */

    const deleteConnectionMutation = useMutation({
        mutationFn: adminDeleteConnection,
        onSuccess: (_data, variables) => {
            queryClient.invalidateQueries({ queryKey: ['adminGetConnection', variables.medicineId, variables.keywordId] });
            queryClient.invalidateQueries({ queryKey: ['adminGetMedicineByKeyword', props.medicineId] });
            queryClient.invalidateQueries({ queryKey: ['adminGetMedicinesByKeyword', props.page, props.size, props.isDisplay, props.searchKeyword] });
            queryClient.invalidateQueries({ queryKey: ['adminGetKeywords'] });
            queryClient.invalidateQueries({ queryKey: ['adminGetKeyword', variables.keywordId] });
            queryClient.invalidateQueries({ queryKey: ['adminAutoCompleteKeyword', props.medicineId] });
        },
        onError: (error: any) => {
            toast({
                title: `${error.response?.data?.message}`,
                status: "error",
                position: "top",
                duration: 2000,
                isClosable: true,
            });
        },
    });

    /* render */

    return (
        <Modal motionPreset="slideInBottom" size={"4xl"} isOpen={props.isOpen} onClose={props.onClose}>
            <ModalOverlay />
            <ModalContent>
                <ModalHeader>
                    <Text color={colors.blue} fontSize="lg" fontWeight="bold">{medicineData?.medicine.nameKor ?? "-"}</Text>
                    <ModalCloseButton />
                </ModalHeader>
                <ModalBody paddingTop={5}>

                    {/* 약 정보 */}

                    <Stack 
                        direction={{
                            base:"column",
                            sm:"column",
                            md:"column",
                            lg:"row",
                            xl:"row"
                        }}
                        width={"100%"} 
                        marginTop={5}
                    >
                        {medicineData ? 
                            (medicineImage && medicineImage !== "NULL") ? (
                            <Image 
                                src={medicineImage.split(";")[0]}
                                alt="medicine" 
                                width={200} 
                                height={200}
                                border={"0.5px solid " + colors.g4}
                                borderRadius={10}
                                objectFit={"contain"}
                            />
                            ) : (
                                <Image 
                                    src="/png/nutrients.png"
                                    alt="medicine" 
                                    width={200} 
                                    height={200}
                                    border={"0.5px solid " + colors.g4}
                                    borderRadius={10}
                                    objectFit={"contain"}
                                />
                        ) : (
                            <Stack width={"100%"} height={"200px"} justifyContent={"center"} alignItems={"center"}>
                                <Spinner color={colors.blue} />
                            </Stack>
                        )}
                        <VStack
                            marginLeft={{
                                base:0,
                                sm:0,
                                md:0,
                                lg:5,
                                xl:5
                            }}
                            marginTop={{
                                base:5,
                                sm:5,
                                md:5,
                                lg:0,
                                xl:0
                            }}
                            justifyContent={"flex-start"}
                            alignItems={"flex-start"}
                        >
                            <HStack>
                                <Text fontSize={16} fontWeight={700} color={colors.g3}>성분 :</Text>
                                <Text fontSize={16} fontWeight={400} color={colors.g1}>{avoidNullString(medicineData?.medicine.ingredientInfo)}</Text>
                            </HStack>
                            <HStack>
                                <Text fontSize={16} fontWeight={700} color={colors.g3}>제조사 / 판매사 :</Text>
                                <Text fontSize={16} fontWeight={400} color={colors.g1}>{avoidNullString(medicineData?.medicine.maker)} / {avoidNullString(medicineData?.medicine.seller)}</Text>
                            </HStack>
                            <HStack>
                                <Text fontSize={16} fontWeight={700} color={colors.g3}>포장단위 :</Text>
                                <Text fontSize={16} fontWeight={400} color={colors.g1}>{avoidNullString(medicineData?.medicine.packageUnit)}</Text>
                            </HStack>   
                            <HStack>
                                <Text fontSize={16} fontWeight={700} color={colors.g3}>카테고리 :</Text>
                                <Text fontSize={16} fontWeight={400} color={colors.g1}>{avoidNullString(medicineData?.medicine.category)}</Text>
                            </HStack>
                        </VStack>
                    </Stack>

                    {/* keyword list */}
            
                    <Stack marginTop={5} width={"100%"} padding={3} justifyContent={"center"} alignItems={"flex-start"}>
                        <Text fontSize={16} fontWeight={400} color={colors.black}>{medicineData?.medicine.connections.length}개의 키워드가 연결됨.</Text>
                        <HStack
                            width={"100%"}
                            paddingY={2}
                            justifyContent={"flex-start"}
                            alignItems={"center"}
                            flexWrap={"wrap"}

                            gap={3}
                        >
                            {medicineData?.medicine.connections.map((connection) => (
                                <Box position="relative" display="inline-block">
                                    <Button 
                                        key={connection.keyword.id}
                                        size={"md"} 
                                        variant={"outline"}
                                        borderColor={colors.blue}
                                        marginRight={1}
                                    >
                                        <Text>{connection.keyword.keyword}</Text>
                                        <Text fontSize={12} fontWeight={500} color={colors.blue} marginLeft={1}>{connection.weight}</Text>
                                    </Button>
                                    <Box
                                        position="absolute"

                                        top="-5px"
                                        right="-5px"

                                        width="18px"
                                        height="18px"

                                        borderRadius="50%"
                                        backgroundColor={colors.black}

                                        display="flex"
                                        alignItems="center"
                                        justifyContent="center"
                                        zIndex={2}

                                        _hover={{ backgroundColor: colors.g1, cursor: "pointer" }}
                                        as="button"
                                        onClick={() => onPressDeleteConnectionBtn(medicineId ?? "", connection.keyword.id)}
                                    >
                                        <MyIcon src={"/svg/delete-line.svg"} alt="trash" width={12} height={12} />
                                    </Box>
                                </Box>
                            ))}
                        </HStack>
                    </Stack>

                    {/* new keyword input */}

                    <HStack marginTop={5} width={"100%"} padding={3} justifyContent={"center"} alignItems={"flex-start"}>
                        <FormControl width={"50%"}>
                            <Input 
                                {...register("newKeyword", { required: true, minLength: 1, maxLength: 20 })}
                                type="text" 
                                placeholder="새로운 키워드 입력"
                            />
                            {errors.newKeyword?.message ? <Text fontSize={12} color={colors.red} padding={2}>{errors.newKeyword?.message}</Text> : null}
                            <Text fontSize={12} color={colors.g3} padding={2}>1자 이상 20자 이하로 입력해주세요.</Text>
                        </FormControl>
                        <FormControl width={"50%"}>
                            <Input 
                                {...register("newKeywordWeight", { required: true, min: 1, max: 100 })}
                                type="number" 
                                placeholder="가중치 입력"
                            />
                            {errors.newKeywordWeight?.message ? <Text fontSize={12} color={colors.red} padding={2}>{errors.newKeywordWeight?.message}</Text> : null}
                            <Text fontSize={12} color={colors.g3} padding={2}>1 이상 100 이하로 입력해주세요.</Text>
                        </FormControl>
                    </HStack>

                    {/* auto complete list */}

                    {!isAutoCompleteLoading ? (
                         <HStack
                            width={"100%"}
                            padding={3}
                            justifyContent={"flex-start"}
                            alignItems={"center"}
                            flexWrap={"wrap"}
                            minHeight={"100px"}
                            maxHeight={"300px"}
                            overflowY={"auto"}
                        >
                        {autoCompleteData?.map((keyword) => (
                            <Button 
                                key={keyword.id}
                                size={"md"} 
                                variant={"outline"}
                                backgroundColor={colors.blue}
                                marginRight={1}
                                onClick={() => onPressAutoCompleteKeywordBtn(keyword.keyword)}
                            >
                                <Text fontSize={16} fontWeight={400} color={colors.white}>{keyword.keyword}</Text>
                            </Button>
                        ))}
                        </HStack>
                    ) : (
                        <HStack
                            width={"100%"}
                            padding={3}
                            justifyContent={"flex-start"}
                            alignItems={"center"}
                            height={"100px"}
                        >
                            <Spinner color={colors.blue} />
                        </HStack>
                    )}

                    {/* button */}

                    <Stack marginTop={5} width={"100%"} padding={3} justifyContent={"center"} alignItems={"center"} gap={3}>
                        <Button 
                            width={"100%"}
                            backgroundColor={colors.white}
                            color={colors.blue}
                            borderWidth={1}
                            borderColor={colors.blue}
                            borderRadius={10}
                            _hover={{ bg: colors.blue, color: colors.white }}
                            boxShadow="sm"

                            disabled={!isValid}
                            onClick={handleSubmit(onSubmitSeqNoOrIngredientCode)}
                            isLoading={createConnectionBySeqNoOrIngredientCodeMutation.isPending}
                        >
                            seqNo + ingredientCode +
                        </Button>
                        <HStack width={"100%"} justifyContent={"center"} alignItems={"center"} gap={3}>
                            <Button
                                width={"50%"}
                                backgroundColor={colors.teal}
                                color={colors.white}
                                borderRadius={10}
                                _hover={{ bg: colors.teal }}
                                boxShadow="sm"

                                disabled={!isValid}
                                onClick={handleSubmit(onSubmitSeqNo)}
                                isLoading={createConnectionBySeqNoMutation.isPending}
                            >
                                seqNo +
                            </Button>
                            <Button
                                width={"50%"}
                                backgroundColor={colors.link}
                                color={colors.white}
                                borderRadius={10}
                                _hover={{ bg: colors.link }}
                                boxShadow="sm"

                                disabled={!isValid}
                                onClick={handleSubmit(onSubmitIngredientCode)}
                                isLoading={createConnectionByIngredientCodeMutation.isPending}
                            >
                                ingredientCode +    
                            </Button>
                        </HStack>
                        <Button 
                            width={"100%"}
                            backgroundColor={colors.blue}
                            color={colors.white}
                            borderRadius={10}
                            _hover={{ bg: colors.blue }}
                            boxShadow="sm"

                            disabled={!isValid}
                            onClick={handleSubmit(onSubmitIndividual)}
                            isLoading={createConnectionMutation.isPending}
                        >
                            개별 추가하기
                        </Button>
                    </Stack>
                </ModalBody>
            </ModalContent>
        </Modal>    
    )
}